/**
 * Стартовая последовательность приложения — экран SYS-17 (CC §4.3).
 *
 *  1. `GET /config` без сессии: лимиты, min_client_versions, реакции,
 *     категории жалоб, legal, captcha, push.
 *  2. Нет сессии → A-01/A-02; цель deep link сохраняется до входа.
 *  3. `GET /me`: `onboarding` → O-07; непустой `consents_required` → SYS-05; 401 → SYS-07.
 *  4–7. Snapshot, перезагрузка списков, cursor, WebSocket — внутри SyncEngine.
 *  8. `POST /push/subscriptions` — только при уже выданном разрешении
 *     (§8.2 п. 3–4): системный запрос идёт лишь из N-03 по жесту пользователя.
 */
import { api, setApiLocale, unwrap } from '@shared/api/client'
import type { Me } from '@shared/api/types'
import { ApiError, isNetworkError } from '@shared/api/errors'
import {
  applyStorageBudgetAtStart,
  getActiveUserId,
  getOrCreateInstallId,
  markWebkitNotifications,
  meRepo,
  mirrorSettingsForWorker,
  openUserDb,
  setActiveUser,
  userDbName,
} from '@shared/db'
import { countInstallSession } from '@shared/pwa/install'
import { retryPushSubscriptionIfFlagged, syncPushSubscription } from '@shared/push'
import { SyncEngine } from '@shared/sync/engine'
import { setSyncEngine, syncEngine } from '@shared/sync/instance'
import { startLeaderElection } from '@shared/ws/leader'

import { useAppStore } from '../model/app-store'
import { usePresenceStore } from '../model/presence-store'
import { useTypingStore } from '../model/typing-store'
import { applyDocumentLanguage } from '../model/locale'
import { startMeMirror } from './me-mirror'
import { compareSemver } from './semver'

// Реэкспорт: экраны импортируют `refreshMe` отсюда исторически и по смыслу.
export { refreshMe } from './refresh-me'
import { setActivationListener } from './activation'
import { attachEngineCallbacks } from './engine-callbacks'
import { handleOwnerChange } from './session-lifecycle'

let started = false

export async function bootstrap(): Promise<void> {
  if (started) return
  started = true

  try {
    await runBootstrap()
  } catch (error) {
    /*
     * Непредвиденная ошибка старта обязана быть ВИДНА. Без этого любая
     * мелочь — недоступный в незащищённом контексте API, отказ IndexedDB
     * в приватном режиме — оставляла бы SYS-17 крутиться вечно, и человек
     * видел бы бесконечную загрузку вместо причины (ПС §19).
     */
    console.error('bootstrap failed', error)
    const store = useAppStore.getState()
    store.setBootError('Не удалось запустить приложение. Обновите страницу.')
    store.setPhase('failed')
  }
}

/** Сколько ждём локальную базу устройства, прежде чем объяснить задержку. */
const DEVICE_DB_TIMEOUT_MS = 6000

/** `true` — задача успела, `false` — вышло время. Сама задача не отменяется. */
export async function withDeadline(task: Promise<unknown>, ms: number): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<false>((resolve) => {
    timer = setTimeout(() => resolve(false), ms)
  })

  try {
    return await Promise.race([task.then(() => true), deadline])
  } finally {
    clearTimeout(timer)
  }
}

async function runBootstrap(): Promise<void> {
  const store = useAppStore.getState()

  // Лидерство нужно до очереди pending: её обрабатывает только одна вкладка.
  startLeaderElection()

  // Признак движка WebKit — ограничивает набор полей showNotification (§8.3).
  const isWebkit = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

  /*
   * Первое обращение к базе устройства может не завершиться: если открыта
   * другая вкладка «Движ» со старой версией базы, обновление схемы блокируется
   * и `open()` ждёт освобождения без ограничения времени. Бесконечная загрузка
   * вместо причины запрещена (ПС §19) — ждём ограниченно и объясняем, что делать.
   */
  const deviceReady = await withDeadline(
    (async () => {
      await markWebkitNotifications(isWebkit)
      await getOrCreateInstallId()
    })(),
    DEVICE_DB_TIMEOUT_MS,
  )

  if (!deviceReady) {
    store.setBootError('Другая вкладка «Движ» держит локальные данные. Закройте её и обновите страницу.')
    store.setPhase('failed')
    return
  }

  /* --- Шаг 1: конфигурация ------------------------------------------- */

  let config
  try {
    config = unwrap(await api.GET('/config'))
  } catch (error) {
    store.setBootError(
      isNetworkError(error)
        ? 'Не удалось связаться с сервисом. Проверьте соединение.'
        : 'Сервис временно недоступен.',
    )
    store.setPhase('failed')
    return
  }

  store.setConfig(config)

  // §9.5 п. 6: версия сравнивается semver, без лексикографики.
  if (compareSemver(__APP_VERSION__, config.min_client_versions.web) < 0) {
    store.setBlocking({ kind: 'client_upgrade_required' })
    store.setPhase('ready')
    return
  }

  /* --- Шаг 3: кто мы ---------------------------------------------------- */

  let me
  try {
    me = unwrap(await api.GET('/me'))
  } catch (error) {
    if (error instanceof ApiError) {
      // 401 без сессии — обычный путь первого запуска, а не ошибка.
      if (error.code === 'unauthenticated' || error.code === 'session_expired') {
        store.setPhase('unauthenticated')
        return
      }
      // account_* обрабатывает глобальный перехватчик (SYS-08 / SYS-09).
      store.setPhase('ready')
      return
    }

    store.setBootError('Не удалось загрузить профиль. Повторите попытку.')
    store.setPhase('failed')
    return
  }

  await startSession(me)
}

/**
 * Запускает пользовательскую сессию: открывает реплику, поднимает движок
 * синхронизации, выставляет фазу приложения.
 */
export async function startSession(me: Me): Promise<void> {
  const store = useAppStore.getState()

  /*
   * CC §4.11 п. 2: вошли под ДРУГИМ `Me.user.id` — база прежнего владельца
   * удаляется целиком, без выборочной чистки. Иначе на устройстве осталась бы
   * чужая переписка, а вкладки продолжили бы работать со старой репликой.
   */
  const previousUserId = await getActiveUserId()
  if (previousUserId && previousUserId !== me.user.id) {
    await handleOwnerChange(previousUserId, me.user.id)
  }

  store.setMe(me)
  setApiLocale(me.settings.locale || 'ru')
  applyDocumentLanguage(me.settings.locale || 'ru')

  await setActiveUser(me.user.id, userDbName(me.user.id))
  /*
   * Свежий `Me` кладётся в реплику сразу: зеркало (me-mirror) читает экраны именно из неё,
   * и без этой записи после обновления клиента интерфейс работал бы со старым снимком
   * (например, без новых полей настроек) до первого события `me.updated`.
   */
  await meRepo.put(me).catch(() => undefined)
  await mirrorSettingsForWorker(me.settings.locale || 'ru', me.settings.notif_preview)

  /*
   * Согласия требуются — SYS-05 поверх интерфейса (BS §15.1). Не во время
   * onboarding: у нового аккаунта `consents_required` заполнен всегда, а сами
   * согласия собирает первый шаг A-05; «Документы обновились» поверх него —
   * ложное сообщение. Если документы сменились до завершения, `complete`
   * ответит 409 `consents`, и O-07 вернёт на тот же шаг.
   */
  if (me.consents_required?.length && me.user.status !== 'onboarding') {
    store.setBlocking({ kind: 'consent_required' })
  }

  /*
   * BS §5.7: в `onboarding` доступны только endpoint'ы с пометкой O — snapshot,
   * списки и WebSocket отвечают 403 `onboarding_incomplete`. Поэтому шаги 4–7
   * (движок синхронизации) запускаются, когда аккаунт стал `active`: сразу или
   * после `POST /me/onboarding/complete` через `refreshMe` → `activation`.
   */
  setActivationListener((activated) => void startSync(activated))

  if (me.user.status === 'onboarding') {
    /*
     * Реплика нужна и до активации: шаг фото хранит обработанные медиа в базе
     * пользователя (`mediaRepo`), а движок откроет ту же базу позже, идемпотентно.
     */
    openUserDb(me.user.id)
    store.setPhase('onboarding')
  } else {
    await startSync(me)
  }

  /*
   * Шаг 8. Подписка регистрируется заново после ЛЮБОГО входа (§8.2 п. 4,
   * BS §8.5 п. 9) и сверяет VAPID-ключ с конфигурацией (§8.2 п. 3, D-38).
   * Разрешение при этом не запрашивается — без него функция молча выходит.
   */
  void syncPushSubscription(store.config)
  void retryPushSubscriptionIfFlagged(store.config)

  // PWA-07 DoD п. 2: предложение установки — не раньше третьего сеанса.
  void countInstallSession(me.user.id)

  /*
   * IOS §1.5 п. 4: бюджеты хранилища по квоте origin и LRU-вычистка истории.
   * Не на критическом пути старта — в простое, когда первый экран уже виден.
   */
  scheduleIdle(() => void applyStorageBudgetAtStart())
}

/**
 * Шаги 4–7 стартовой последовательности: snapshot, списки, cursor, WebSocket.
 * Идемпотентно — повторный вызов для уже запущенного движка ничего не делает.
 */
async function startSync(me: Me): Promise<void> {
  const store = useAppStore.getState()
  if (syncEngine()) {
    if (store.phase === 'onboarding') store.setPhase('ready')
    return
  }

  /*
   * Свежий `GET /me` передаётся движку как начальное состояние реплики: только он
   * несёт `user.device_id` текущей сессии (кадр hello, BS §9.3), а при обычном
   * запуске snapshot не запрашивается — без этого hello ушёл бы с записью прошлого сеанса.
   */
  const engine = new SyncEngine(attachEngineCallbacks())
  setSyncEngine(engine)
  // Кадры typing — в общий стор: индикатор нужен и в диалоге, и в списке чатов.
  engine.onTypingFrame((frame) => useTypingStore.getState().apply(frame, me.user.id))
  // Кадры presence — тоже в стор: статусы «в сети» живут в «Контактах» и списке чатов.
  engine.onPresenceFrame((frame) => usePresenceStore.getState().apply(frame))
  await engine.start(me.user.id, me)

  // Дальше `Me` живёт от реплики: сюда попадают и `me.updated` с других устройств.
  startMeMirror(me.user.id)

  store.setPhase('ready')
}

function scheduleIdle(task: () => void): void {
  const idle = (
    globalThis as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }
  ).requestIdleCallback
  if (idle) idle(task, { timeout: 5000 })
  else setTimeout(task, 2000)
}
